import { motion, AnimatePresence } from "motion/react";
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import { Link, Outlet, useLocation, useNavigate, useNavigationType, useOutlet } from "react-router-dom";
import { CustomCursor } from "./CustomCursor";
import { Menu, X } from "lucide-react";
import { AnimatedDivider } from "./AnimatedDivider";
import { XIcon, InstagramIcon, LinkedinIcon, FacebookIcon, GithubIcon, SoundcloudIcon, SpotifyIcon, MailIcon } from "./SocialIcons";
import { MarqueeBanner } from "./MarqueeBanner";
import { TacitonicRadio } from "./TacitonicRadio";
import { MagneticButton } from "./MagneticButton";
import { buildCloudflareSrcSet, handleCloudflareImgError } from "../utils/cloudflareImage";

// Track global scrolled pop state synchronously
let globalIsScrolledPop = false;

// We'll also track the last known scroll position
let lastScrollY = 0;
if (typeof window !== "undefined") {
  window.addEventListener("scroll", () => {
    lastScrollY = window.scrollY;
  }, { passive: true });
}

const NAV_LINKS = [
  { name: "Home", href: "/" },
  { name: "About", href: "/about" },
  { name: "Discography", href: "/discography" },
  { name: "Services", href: "/services" },
  { name: "Apparatus", href: "/apparatus" },
  { name: "Contact", href: "/contact" },
];

const SOCIAL_LINKS = [
  { name: "Spotify", href: "https://open.spotify.com/user/rzf081j9u6ox8z3r4rfivw7hs?si=7dd2a46eac434c08", icon: SpotifyIcon },
  { name: "Instagram", href: "https://www.instagram.com/tacitonic/", icon: InstagramIcon },
  { name: "LinkedIn", href: "https://www.linkedin.com/in/atharvadhekne/", icon: LinkedinIcon },
  { name: "X", href: "https://twitter.com/tacitonic", icon: XIcon },
  { name: "Facebook", href: "https://www.facebook.com/tacitonic", icon: FacebookIcon },
  { name: "GitHub", href: "https://github.com/tacitonic", icon: GithubIcon },
  { name: "SoundCloud", href: "https://soundcloud.com/tacitonic", icon: SoundcloudIcon },
  { name: "Email", href: "mailto:atharva@tacitonic.com", icon: MailIcon },
];

export function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileClickedLink, setMobileClickedLink] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 1024 : false);
  const [navCount, setNavCount] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLightMode, setIsLightMode] = useState(false);

  // Synchronous Scrolled Pop calculation
  const isScrolledPopCalculated = navigationType === 'POP' && lastScrollY > 100 && !isMobile;

  const isFromMobile = location.state?.fromMobile;

  const handleNavClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    if (location.pathname === href) return;

    // Lock scrolling immediately
    const preventScroll = (ev: Event) => ev.preventDefault();
    window.addEventListener('wheel', preventScroll, { passive: false });
    window.addEventListener('touchmove', preventScroll, { passive: false });
    
    // Safety cleanup
    const cleanup = () => {
      window.removeEventListener('wheel', preventScroll);
      window.removeEventListener('touchmove', preventScroll);
    };

    if (window.scrollY > 5) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      const checkScroll = setInterval(() => {
        if (window.scrollY <= 20) {
          clearInterval(checkScroll);
          window.scrollTo(0, 0); 
          setTimeout(() => {
            navigate(href, { state: { fromMobile: false, fromNav: true } });
            setTimeout(cleanup, 1200);
          }, 30);
        }
      }, 20);

      setTimeout(() => {
        clearInterval(checkScroll);
        window.scrollTo(0, 0);
        setTimeout(() => {
          navigate(href, { state: { fromMobile: false, fromNav: true } });
          setTimeout(cleanup, 1200);
        }, 30);
      }, 1000);
    } else {
      window.scrollTo(0, 0);
      setTimeout(() => {
        navigate(href, { state: { fromMobile: false, fromNav: true } });
        setTimeout(cleanup, 1200);
      }, 20);
    }
  }, [location.pathname, navigate]);

  const contextValues = useMemo(() => ({
    isFromMobile,
    hasLoaded,
    isLightMode,
    handleNavClick,
    isScrolledPop: isScrolledPopCalculated,
    isMobile
  }), [isFromMobile, hasLoaded, isLightMode, handleNavClick, isScrolledPopCalculated, isMobile]);

  const currentOutlet = useOutlet(contextValues);
  
  // Frozen State Architecture 
  const [displayState, setDisplayState] = useState({
    location: location,
    outlet: currentOutlet
  });

  useLayoutEffect(() => {
    if (location.pathname === displayState.location.pathname) {
      setDisplayState(prev => {
        if (prev.outlet === currentOutlet) return prev;
        return { ...prev, outlet: currentOutlet };
      });
    }
  }, [currentOutlet, location.pathname, displayState.location.pathname]);

  useLayoutEffect(() => {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    if (location.pathname !== displayState.location.pathname) {
      setNavCount(prev => prev + 1);
      
      const onPop = navigationType === 'POP' && window.scrollY > 200;
      
      if (onPop && isMobile) {
        // Freeze visual layout, smoothly scroll up, then trigger the change. (Mobile kept as is)
        const preventScroll = (ev: Event) => {
          if (ev.cancelable) ev.preventDefault();
        };
        window.addEventListener('wheel', preventScroll, { passive: false });
        window.addEventListener('touchmove', preventScroll, { passive: false });

        window.scrollTo({ top: 0, behavior: 'smooth' });

        const checkScroll = setInterval(() => {
          if (window.scrollY <= 10) {
            clearInterval(checkScroll);
            window.scrollTo(0, 0);
            window.removeEventListener('wheel', preventScroll);
            window.removeEventListener('touchmove', preventScroll);
            
            // Wait a tiny bit for the browser to settle after scroll
            setTimeout(() => {
               setDisplayState({ location, outlet: currentOutlet });
            }, 50);
          }
        }, 50);

        setTimeout(() => {
          clearInterval(checkScroll);
          window.scrollTo(0, 0);
          window.removeEventListener('wheel', preventScroll);
          window.removeEventListener('touchmove', preventScroll);
          setDisplayState({ location, outlet: currentOutlet });
        }, 1100);
      } else {
        // For non-POP, or already at top, or Desktop Scrolled Pop (isScrolledPop) -> instant change!
        if (window.scrollY > 0) window.scrollTo(0, 0);
        setDisplayState({ location, outlet: currentOutlet });
      }
    }
  }, [location.pathname, displayState.location.pathname, navigationType, currentOutlet]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Hide initial HTML loader smoothly before showing React content
    const loader = document.getElementById('initial-loader');
    const isCrawler = typeof navigator !== 'undefined' && /ia_archiver|archive\.org_bot|wayback|googlebot|bingbot|crawler|spider/i.test(navigator.userAgent);
    if (isCrawler) {
      if (loader) loader.classList.add('loader-hidden');
      setHasLoaded(true);
    } else if (loader) {
      setTimeout(() => {
        loader.classList.add('loader-hidden');
        setTimeout(() => {
          setHasLoaded(true);
        }, 300); // Trigger page content entrance animations right as loader fades out
      }, 500); // Give the loader 500ms to show
    } else {
      setHasLoaded(true);
    }
    
    // Check if user previously set a theme
    if (localStorage.getItem('theme') === 'light') {
      setIsLightMode(true);
      document.documentElement.classList.add('light-theme');
    }
  }, []);

  const toggleTheme = () => {
    if (isLightMode) {
      document.documentElement.classList.remove('light-theme');
      localStorage.setItem('theme', 'dark');
      setIsLightMode(false);
    } else {
      document.documentElement.classList.add('light-theme');
      localStorage.setItem('theme', 'light');
      setIsLightMode(true);
    }
  };

  return (
    <div className="relative min-h-screen font-sans text-white selection:bg-white selection:text-black overflow-x-hidden cursor-none">
      <CustomCursor />
      
      {/* Navigation */}
      <nav data-nosnippet className="fixed top-0 left-0 w-full z-50 bg-black">
        <div className="px-6 py-6 flex justify-between items-center relative">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, ease: [0.19, 1, 0.22, 1] }}
            className="z-50 shrink-0 mr-4"
          >
            <a href="/" aria-label="Home" className="hover-target block" onClick={(e) => { setIsMobileMenuOpen(false); handleNavClick(e, '/'); }}>
              <img 
                src="https://media.tacitonic.com/cdn-cgi/image/width=400,quality=75,format=auto/media/tacitonic-banner.webp?v=7" 
                srcSet="https://media.tacitonic.com/cdn-cgi/image/width=400,quality=75,format=auto/media/tacitonic-banner.webp?v=7 400w, https://media.tacitonic.com/cdn-cgi/image/width=480,quality=75,format=auto/media/tacitonic-banner.webp?v=7 480w, https://media.tacitonic.com/cdn-cgi/image/width=794,quality=75,format=auto/media/tacitonic-banner.webp?v=7 794w"
                sizes="(max-width: 639px) 240px, (max-width: 1023px) 320px, 397px"
                width={397}
                height={40}
                decoding="async"
                fetchPriority="high"
                alt="tacitonic logo" 
                className="w-[240px] sm:w-[320px] lg:w-auto lg:h-10 h-auto transition-all duration-500 light-theme-invert object-contain"
              />
            </a>
          </motion.div>

          {/* Header Controls Container */}
          <div className="flex items-center gap-8 justify-end ml-auto">
            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8">
              {NAV_LINKS.map((link, i) => {
                const itemKey = isScrolledPopCalculated ? `${link.name}-pop-${navCount}` : link.name;
                return (
                <motion.div
                  key={itemKey}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ 
                    duration: 1.5,
                    delay: i * 0.1,
                    ease: [0.19, 1, 0.22, 1]
                  }}
                >
                  <a
                    href={link.href}
                    aria-label={link.name}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="font-display font-bold text-[12px] tracking-[0.05em] uppercase hover:opacity-50 transition-opacity hover-target block"
                  >
                    <motion.span
                      layoutId={(isMobile || isScrolledPopCalculated) ? undefined : `page-title-${link.name}`}
                      initial={link.name === "Home" ? { x: 10, opacity: 0 } : false}
                      animate={{ 
                        opacity: displayState.location.pathname === link.href ? 0 : 0.6,
                        x: 0 
                      }}
                      transition={{ 
                        duration: 0.4, 
                        ease: "easeOut",
                        layout: { duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }
                      }}
                      className="text-white block"
                      style={{ 
                        pointerEvents: displayState.location.pathname === link.href ? 'none' : 'auto',
                        visibility: displayState.location.pathname === link.href ? 'hidden' : 'visible'
                      }}
                    >
                      {link.name}
                    </motion.span>
                  </a>
                </motion.div>
              )})}
            </div>

            {/* Theme Toggle (Header - Desktop Only) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.5, ease: [0.19, 1, 0.22, 1] }}
              className="hidden lg:block flex-shrink-0 ml-1"
            >
              <button 
                onClick={toggleTheme} 
                className="w-6 h-6 md:w-10 md:h-10 bg-transparent flex items-center justify-center transition-all duration-300 hover-target cursor-none overflow-hidden group hover:scale-110 lg:mr-0 z-50 shrink-0"
                aria-label="Toggle Theme"
              >
                <img 
                  src="https://media.tacitonic.com/media/invert-mode.svg?v=7" 
                  alt="Toggle Theme" 
                  width={20}
                  height={20}
                  className={`w-3 h-3 md:w-5 md:h-5 transition-transform duration-500 ease-[0.19,1,0.22,1] ${isLightMode ? 'invert-0 rotate-180' : 'invert rotate-0'}`} 
                />
              </button>
            </motion.div>

            {/* Mobile Menu Toggle */}
            <button 
              className="lg:hidden z-50 hover-target p-2 -mr-3 text-white"
              aria-label="Toggle mobile menu"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
                  <line x1="4" y1="8" x2="20" y2="8" />
                  <line x1="8" y1="16" x2="20" y2="16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Animated Bottom Border - Truly edge-to-edge */}
        <div className="absolute bottom-0 left-0 w-full">
          {!hasLoaded ? (
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.5, ease: [0.19, 1, 0.22, 1] }}
              className="h-[3px] w-screen bg-white origin-left"
            />
          ) : (
            <div className="h-[3px] w-screen bg-white" />
          )}
        </div>
      </nav>

      {/* Mobile Menu Overlay - Completely New Architecture */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div 
              key="mobile-bg"
              initial={{ y: "-100%" }}
              animate={{ y: 0 }}
              exit={{ y: "-100%" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-0 z-40 bg-black lg:hidden"
            />
            <motion.div 
              key="mobile-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 pt-32 px-6 flex flex-col gap-8 lg:hidden pointer-events-none"
            >
              {NAV_LINKS.map((link, i) => {
                const isCurrentPage = displayState.location.pathname === link.href;
                const isClicked = mobileClickedLink === link.name;
                const shouldHaveLayoutId = !(isCurrentPage && !isClicked);
                
                return (
                  <motion.div 
                    key={link.name} 
                    className="w-fit pointer-events-auto"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: isClicked ? 1 : 0, transition: { duration: 0.2 } }}
                    transition={{ duration: 0.4, delay: 0.1 + (i * 0.05), ease: "easeOut" }}
                  >
                    <a
                      href={link.href}
                      aria-label={link.name}
                      onClick={(e) => {
                        e.preventDefault();
                        if (isCurrentPage) {
                          setIsMobileMenuOpen(false);
                          return;
                        }
                        setMobileClickedLink(link.name);
                        
                        // IMPORTANT: Scroll to top instantly before rendering the new page
                        // Since the menu covers the screen, the user won't see the jump.
                        // This prevents the overlapping z-axis grid bug.
                        window.scrollTo(0, 0);
                        
                        navigate(link.href, { state: { fromMobile: true, fromNav: true } });
                        
                        // Keep the menu open for a split second while the page mounts instantly behind it,
                        // then slide the menu up to reveal the new page.
                        setTimeout(() => {
                          setIsMobileMenuOpen(false);
                          // Reset clicked link after animation finishes
                          setTimeout(() => setMobileClickedLink(null), 500);
                        }, 100);
                      }}
                      className="hover-target w-fit block relative"
                    >
                      <motion.h1 
                        layoutId={shouldHaveLayoutId ? `page-title-${link.name}` : undefined}
                        className="font-display text-[30px] font-bold tracking-tighter leading-none uppercase m-0 p-0 origin-left transition-colors duration-500"
                        style={{ color: isCurrentPage ? (isLightMode ? "#000000" : "#FEFDF9") : (isLightMode ? "#cccccc" : "#666666") }}
                      >
                        {link.name}
                      </motion.h1>
                    </a>
                  </motion.div>
                );
              })}
              
              {/* Mobile Theme Toggle inside Menu */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, delay: 0.4, ease: "easeOut" }}
                className="mt-4 pointer-events-auto w-fit"
              >
                <div className="flex items-center gap-4 text-white/50">
                  <button 
                    onClick={toggleTheme} 
                    className="w-12 h-12 bg-transparent flex items-center justify-center transition-all duration-300 hover-target cursor-none overflow-hidden group hover:scale-110"
                    aria-label="ToggleTheme"
                  >
                    <img 
                      src="https://media.tacitonic.com/media/invert-mode.svg?v=7" 
                      alt="Toggle Theme" 
                      width={32}
                      height={32}
                      className={`w-8 h-8 transition-transform duration-500 ease-[0.19,1,0.22,1] ${isLightMode ? 'invert-0 rotate-180' : 'invert rotate-0'}`} 
                    />
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="grid w-full relative z-10 lg:mb-[220px] bg-black pointer-events-none">
        <div className="pointer-events-auto w-full grid">
          <AnimatePresence>
            <motion.div
              key={displayState.location.pathname}
              initial={{ opacity: (!hasLoaded || (isScrolledPopCalculated && !isMobile)) ? 0 : 1, zIndex: 10 }}
              animate={{ opacity: 1, zIndex: 10 }}
              exit={isMobile ? { opacity: 0, display: "none", zIndex: 0 } : { opacity: 0, zIndex: 0 }}
              className="col-start-1 row-start-1 w-full min-h-screen relative overflow-hidden bg-black pb-10 md:pb-20"
              transition={{ duration: isMobile ? 0.05 : 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
            >
              {displayState.outlet}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <TacitonicRadio variant="floating" />

      {/* Parallax Curtain Footer Container */}
      <footer className="relative lg:fixed bottom-0 left-0 w-full z-0 flex flex-col lg:h-[220px] pointer-events-auto">
        <div className="shrink-0"><MarqueeBanner text="New York City • Los Angeles • Nashville • Mumbai • Pune • Delhi • Chennai • Bangalore • Kolkata" /></div>

        <div className="flex-1 flex flex-col justify-center px-6 py-6 md:py-8 bg-black text-white border-t border-white/20">
          <div className="max-w-7xl mx-auto w-full flex flex-col md:flex-row justify-between items-center gap-4 font-mono text-xs uppercase tracking-normal">
            <div className="hover-sike cursor-default font-sans text-[13px] opacity-80 font-medium tracking-tight">© {new Date().getFullYear()} <span className="lowercase leading-none">tacitonic</span></div>
            <div className="flex gap-6 opacity-60">
              {SOCIAL_LINKS.map(link => {
                const Icon = link.icon;
                return (
                  <a key={link.name} href={link.href} target="_blank" rel="noopener noreferrer" className="hover:opacity-100 transition-opacity hover-target" aria-label={link.name}>
                    <Icon size={20} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
